import type { PadOutcome } from '@/api/endpoints/qualifications';
import { canCall } from '@/services/dialer/dialRules';

/**
 * The outcome pad, keys 1 to 8 (spec §4.5). The server translates each one
 * into the stored call (server/src/domain/calling/qualification.ts); the
 * page only needs what each one asks of the caller.
 */
export interface PadEntry {
  key: string;
  outcome: PadOutcome;
  /** dialer:callPage.outcomes.<outcome> */
  labelKey: string;
  /** Something must be filled before it can be saved. */
  needs: 'meeting' | 'callback' | 'confirm' | null;
}

export const PAD: readonly PadEntry[] = [
  { key: '1', outcome: 'meeting_booked', labelKey: 'meeting_booked', needs: 'meeting' },
  { key: '2', outcome: 'callback_requested', labelKey: 'callback_requested', needs: 'callback' },
  { key: '3', outcome: 'interested', labelKey: 'interested', needs: null },
  { key: '4', outcome: 'not_interested', labelKey: 'not_interested', needs: null },
  { key: '5', outcome: 'gatekeeper', labelKey: 'gatekeeper', needs: null },
  { key: '6', outcome: 'left_voicemail', labelKey: 'left_voicemail', needs: null },
  { key: '7', outcome: 'do_not_call', labelKey: 'do_not_call', needs: 'confirm' },
  { key: '8', outcome: 'wrong_number', labelKey: 'wrong_number', needs: null },
] as const;

export const padEntry = (outcome: PadOutcome): PadEntry => PAD.find((p) => p.outcome === outcome)!;

/**
 * What the outcome does to the prospect and its last call once committed,
 * in the shapes canCall reads (server: toCallPatch + the do-not-call block;
 * a wrong number marks the number unassigned).
 */
export function effectOf(outcome: PadOutcome): { prospect: Record<string, unknown>; lastCall: Record<string, unknown> } {
  const prospect: Record<string, unknown> = { id: 'p', prospect_phone: '+33612345678', status: 'active', source: 'Revcall Client Dashboard' };
  const lastCall: Record<string, unknown> = { call_status: 'completed', call_outcome: outcome, meeting_booked: false };
  if (outcome === 'meeting_booked') Object.assign(lastCall, { call_outcome: 'contacted', meeting_booked: true });
  if (outcome === 'do_not_call') {
    lastCall.call_outcome = 'not_interested';
    Object.assign(prospect, { status: 'do_not_call', do_not_call: true });
  }
  if (outcome === 'wrong_number') prospect.phone_unassigned = true;
  return { prospect, lastCall };
}

/**
 * ENG-6: an AI suggestion may preselect an outcome only if the prospect stays
 * dialable after it. Anything that takes the prospect out of auto-dial needs
 * a deliberate human choice. Derived from canCall, never from a list.
 */
export function aiMayPreselect(outcome: PadOutcome): boolean {
  const { prospect, lastCall } = effectOf(outcome);
  return canCall(prospect, lastCall, { selectedSource: 'Revcall Client Dashboard' });
}

/**
 * DES-5: a suggestion fills only an untouched pad; a human choice always wins
 * and a late suggestion never replaces it.
 */
export function applySuggestion(
  current: { outcome: PadOutcome | null; source: 'human' | 'ai' | null },
  suggested: PadOutcome | null,
): { outcome: PadOutcome | null; source: 'human' | 'ai' | null } {
  if (!suggested || current.source === 'human' || current.outcome) return current;
  if (!aiMayPreselect(suggested)) return current;
  return { outcome: suggested, source: 'ai' };
}
